import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { useTheme } from '../../theme';
import { Icon } from '@doxyme/icons';
import { useIntl } from '@doxyme/locale';
import { useStatusStyles } from './status.styles';
import { Spinner as SpinnerComponent } from '../spinner';
export const Status = ({ statusLevel, color, className, themeName, size = 24, value, mode = 'green' })=>{
    const theme = useTheme(themeName);
    const { formatMessage } = useIntl();
    const styles = useStatusStyles();
    /**
   * The filled icon variants use a single compound SVG path where the inner
   * symbol (checkmark, exclamation, etc.) is "cut out" via winding rules,
   * leaving it transparent. To give the cutout a visible fill color, we render
   * a backing SVG circle (r=9, slightly inset from the icon's r=10 circle) in
   * the same coordinate space so they rasterize identically — avoiding the
   * subpixel mismatch that CSS backgrounds (e.g. radial-gradient) can exhibit
   * at certain browser zoom levels.
   */ const renderFilledIcon = ({ IconComponent, iconColor, fillColor })=>/*#__PURE__*/ _jsxs("span", {
            css: {
                position: 'relative',
                display: 'inline-flex',
                flexShrink: 0
            },
            className: className,
            children: [
                /*#__PURE__*/ _jsx("svg", {
                    viewBox: "0 0 24 24",
                    width: size,
                    height: size,
                    css: {
                        position: 'absolute'
                    },
                    "aria-hidden": true,
                    children: /*#__PURE__*/ _jsx("circle", {
                        cx: "12",
                        cy: "12",
                        r: "9",
                        fill: fillColor
                    })
                }),
                /*#__PURE__*/ _jsx(IconComponent, {
                    css: {
                        ...styles.icon,
                        position: 'relative',
                        path: {
                            fillRule: 'evenodd'
                        }
                    },
                    style: {
                        color: iconColor
                    },
                    type: "filled",
                    size: size,
                    color: iconColor,
                    "aria-hidden": true
                })
            ]
        });
    switch(statusLevel){
        case 'success':
            {
                return renderFilledIcon({
                    IconComponent: Icon.Checkmark,
                    iconColor: color ?? theme.status.positive,
                    fillColor: theme.status.baseDark
                });
            }
        case 'warning':
            {
                return renderFilledIcon({
                    IconComponent: Icon.Error,
                    iconColor: color ?? theme.status.notice,
                    fillColor: theme.status.baseDark
                });
            }
        case 'info':
            {
                return renderFilledIcon({
                    IconComponent: Icon.Info,
                    iconColor: color ?? theme.status.informative,
                    fillColor: theme.status.baseLight
                });
            }
        case 'error':
            {
                return renderFilledIcon({
                    IconComponent: Icon.Error,
                    iconColor: color ?? theme.status.negative,
                    fillColor: theme.status.baseLight
                });
            }
        case 'spinner':
            {
                return /*#__PURE__*/ _jsx(SpinnerComponent, {
                    size: size - 4,
                    mode: mode,
                    "aria-label": formatMessage({
                        id: 'status.loading'
                    })
                });
            }
        case 'loader':
            {
                return /*#__PURE__*/ _jsx(SpinnerComponent, {
                    value: value,
                    variant: "determinate",
                    size: size - 4,
                    mode: mode,
                    "aria-label": formatMessage({
                        id: 'status.loading'
                    })
                });
            }
        default:
            {
                return null;
            }
    }
};

//# sourceMappingURL=status.js.map